/**
 * Shared types and utilities for provider draft forms.
 *
 * Used by:
 * - AddProviderModalV2 (provider directory modal)
 * - ProviderSearchInline (case ingestion treatment step)
 * - useProviderResolveSearch (resolve search hook)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DraftSource = 'internal' | 'google' | 'manual';

export interface ProviderDraft {
  source: DraftSource;
  baseProviderServiceId?: string | null;
  googlePlaceId?: string | null;
  name: string;
  formattedAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude?: number | null;
  longitude?: number | null;
  phone: string;
  fax: string;
  website: string;
}

export interface ResolveProviderMatch {
  provider_service_id: string;
  name: string;
  formatted_address?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string | null;
  /** Pre-computed subtitle from backend (e.g. "Troy, MI · Verified fax") */
  subtitle?: string | null;
  /** Number of verified ContactPoints across all locations */
  verified_contact_count?: number;
  /** Number of matters linked to this service */
  matter_link_count?: number;
  /** Whether this service has a preferred records destination */
  has_preferred_records?: boolean;
  /** Whether this service has a preferred bills destination */
  has_preferred_bills?: boolean;
  /** Whether this service is already linked to the current matter */
  already_on_matter?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const EMPTY_DRAFT: ProviderDraft = {
  source: 'manual',
  baseProviderServiceId: null,
  googlePlaceId: null,
  name: '',
  formattedAddress: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  latitude: null,
  longitude: null,
  phone: '',
  fax: '',
  website: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const composeAddress = (d: ProviderDraft): string => {
  const l1 = d.addressLine1.trim();
  const l2 = d.addressLine2.trim();
  const city = d.city.trim();
  const state = d.state.trim();
  const zip = d.postalCode.trim();

  if (!l1 && !l2 && !city && !state && !zip) {
    return d.formattedAddress.trim();
  }

  const parts: string[] = [];
  if (l1) parts.push(l1);
  if (l2) parts.push(l2);
  const cityStateZip = [city, state, zip].filter(Boolean).join(', ');
  if (cityStateZip) parts.push(cityStateZip);

  return parts.join(', ');
};

export const parseAddressComponents = (formatted: string) => {
  const value = formatted.trim();
  if (!value) {
    return {
      addressLine1: '',
      city: '',
      state: '',
      postalCode: '',
    };
  }

  // Typical: "123 Main St, City, ST 12345"
  const commaPattern =
    /^(.*?),\s*([^,]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?$/i;
  const commaMatch = value.match(commaPattern);
  if (commaMatch) {
    return {
      addressLine1: commaMatch[1]?.trim() ?? '',
      city: commaMatch[2]?.trim() ?? '',
      state: (commaMatch[3] ?? '').toUpperCase(),
      postalCode: commaMatch[4]?.trim() ?? '',
    };
  }

  // No comma between street/city:
  // "44045 Riverside Pkwy Leesburg, VA 20176"
  const loosePattern =
    /^(.*)\s+([A-Za-z.' -]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?$/i;
  const looseMatch = value.match(loosePattern);
  if (looseMatch) {
    return {
      addressLine1: looseMatch[1]?.trim() ?? '',
      city: looseMatch[2]?.trim() ?? '',
      state: (looseMatch[3] ?? '').toUpperCase(),
      postalCode: looseMatch[4]?.trim() ?? '',
    };
  }

  return {
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
  };
};

export const firstNonEmpty = (...values: Array<string | null | undefined>) =>
  values.find((value) => Boolean(value && value.trim())) ?? '';

export const toProviderTypeHint = (providerType: 'hospital' | 'clinic') =>
  providerType === 'hospital' ? 'HOSPITAL_SYSTEM' : 'CLINIC_INDEPENDENT';
